export const pitchdeck = {
  seo: {
    title: "Bespire - Pitch Deck",
    description: "AI-native creator platform for European creators",
    keywords: "creator platform, AI, european market, content creators"
  },
  navigation: {
    summary: "Summary",
    market: "Market",
    revenue: "Revenue",
    product: "Product",
    team: "Team",
    financial: "Financial",
    logoAlt: "Bespire logo"
  },
  hero: {
    title: "Bespire",
    subtitle: "AI-native creator platform for European creators"
  },
  problem: {
    badge: "The Problem",
    title: "Why do we need Bespire?",
    description: "European content creators face challenges in managing and growing their revenue.",
    points: [
      "No unified tool for revenue management",
      "Complex VAT and invoicing systems",
      "Limited localized payment solutions"
    ]
  },
  solution: {
    badge: "The Solution",
    title: "How Bespire helps",
    description: "AI-powered platform that builds a complete online presence in minutes.",
    features: [
      {
        title: "AI-builder",
        description: "Automatic website and offer structure building with AI assistance"
      },
      {
        title: "Localized payments",
        description: "EU-compatible VAT handling and integrated payment solutions"
      },
      {
        title: "Simple management",
        description: "Everything in one place - content, services, community"
      }
    ]
  },
  market: {
    badge: "Market Opportunity",
    title: "A huge market awaits",
    description: "The European creator economy is growing dynamically.",
    stats: [
      {
        value: "2.4M",
        label: "Available creators in the EU"
      },
      {
        value: "€2.0B",
        label: "Total market size annually"
      }
    ],
    trends: {
      title: "Market Trends",
      points: [
        "The European creator economy market is 51B USD in 2024",
        "The number of micro-influencers is continuously growing",
        "40% of creators actively monetize"
      ]
    }
  },
  businessModel: {
    badge: "Revenue Model",
    title: "How we make money",
    description: "Hybrid revenue model with SaaS subscriptions and transaction fees.",
    streams: [
      {
        percentage: "70%",
        title: "SaaS subscriptions",
        description: "Monthly subscription fees of 50-100 USD per creator"
      },
      {
        percentage: "30%",
        title: "Transaction fees",
        description: "4% average fee on sales processed through the platform"
      }
    ]
  },
  traction: {
    badge: "Progress",
    title: "What we've achieved",
    description: "Key milestones from the past period.",
    milestones: [
      {
        date: "2024 Q1",
        title: "Platform MVP",
        description: "First working version completed with AI-builder"
      },
      {
        date: "2024 Q2",
        title: "First customers",
        description: "Pilot program launched with selected creators"
      },
      {
        date: "2024 Q3",
        title: "Payment integration",
        description: "EU-compatible payment system deployed"
      }
    ]
  },
  team: {
    badge: "The Team",
    title: "Who are we?",
    description: "Experienced professionals from tech and creator worlds.",
    members: [
      {
        name: "Founder 1",
        role: "CEO & Co-founder",
        bio: "10+ years of experience in tech startups"
      },
      {
        name: "Founder 2",
        role: "CTO & Co-founder",
        bio: "Former tech lead at leading companies"
      }
    ]
  },
  ask: {
    badge: "Funding",
    title: "What do we need?",
    description: "2M EUR seed investment to secure 24-month runway.",
    amount: "€2M",
    equity: "Max. 10% equity",
    valuation: "€16M",
    useOfFunds: {
      title: "Use of Funds",
      items: [
        {
          category: "Team Building",
          percentage: "50-55%"
        },
        {
          category: "Marketing & Growth",
          percentage: "20-25%"
        },
        {
          category: "Infrastructure & AI",
          percentage: "15-20%"
        }
      ]
    }
  },
  contact: {
    title: "Let's get in touch",
    description: "Ready to build the future together?",
    emailLabel: "Email",
    email: "hello@bespire.io",
    phoneLabel: "Phone",
    phone: "+36 20 123 4567"
  },
  summary: {
    title: "Executive Summary",
    mission: {
      title: "Mission",
      description: "Bespire is an AI-native creator platform that helps European and Middle Eastern content creators and their managers build a complete, revenue-generating online presence in minutes – with localized payments, invoicing, and offerings."
    },
    product: {
      title: "Current Product Status",
      ready: "Ready",
      readyItems: [
        "AI-builder (website + offer structure)",
        "EU-compatible VAT handling",
        "Integrated online payment"
      ],
      nextMilestones: "Next Milestones",
      milestones: [
        "AI sales assistant (what to sell, pricing, packaging)",
        "AI assistant scaling (multiple languages, verticals, automated campaigns)"
      ]
    },
    keyNumbers: {
      title: "Key Numbers (3-year plan)",
      avgRevenue: "Average platform revenue per paying creator",
      avgRevenueValue: "50–100 USD / month",
      yearlyRevenue: "Annual revenue (SaaS + transaction fee)",
      year1: "240k EUR",
      year2: "1.08M EUR",
      year3: "3.72M EUR",
      ebitda: "EBITDA",
      ebitdaYear1: "–110k EUR",
      ebitdaYear2: "+330k EUR",
      ebitdaYear3: "+2.22M EUR"
    },
    funding: {
      title: "Funding",
      target: "Target: ~2M EUR seed",
      equity: "Maximum equity: 10%",
      valuation: "≈ 18M EUR post-money, ~16M pre-money",
      goal: "Goal: 24-month runway + strong growth marketing and AI development"
    }
  },
  revenue: {
    title: "Revenue Model",
    saas: {
      title: "SaaS Subscriptions",
      range: "50–100 USD/month / creator",
      starter: "Starter: low entry, limited products, low AI quota",
      pro: "Pro: main focus package (~70 EUR/month), more products, more AI features",
      agency: "Agency: multiple creator management, extra reports"
    },
    transaction: {
      title: "Transaction Fee",
      average: "Average 4%",
      free: "Free package: 9% fee (highest)",
      paid: "Paid packages: tiered, decreasing fees"
    },
    addons: {
      title: "AI Add-ons and Services",
      credits: "Extra AI credits (video scripts, workshop outlines, email funnels, sales pages)",
      services: "Done-with-you setup, agency packages"
    }
  },
  product: {
    title: "Product & Roadmap",
    current: {
      title: "Currently Ready",
      aiBuilder: {
        title: "AI-builder",
        features: [
          "Q&A-based assessment of creator goals, audience, and strengths",
          "Generates complete website structure, offer packages, and base content"
        ]
      },
      vat: {
        title: "VAT Handling and Invoicing Logic",
        description: "EU-compliant VAT calculation and invoicing across different countries"
      },
      payment: {
        title: "Online Payment Integration",
        description: "EUR focus with HU support"
      }
    },
    next: {
      title: "Next Major Developments",
      phase1: {
        title: "AI Assistant (sales & ops) – 0–12 months",
        features: [
          "\"What should I sell?\" – offer strategy, pricing, packaging",
          "Campaign ideas: launch calendar, promotions, post ideas",
          "\"Next best action\" suggestions based on real data"
        ]
      },
      phase2: {
        title: "AI Assistant Scaling – 12–36 months",
        features: [
          "Multiple languages (DE, PL, RO, etc.)",
          "Deeper vertical specialization (fitness, education, business coaches, etc.)",
          "Semi-autonomous campaign execution (offers, landing pages, email sequences)"
        ]
      }
    }
  },
  target: {
    title: "Target Market & Positioning",
    personas: {
      title: "Main Personas",
      items: [
        "10–200k follower lifestyle, fitness, education creators in Europe's Eurozone + HU",
        "Online coaches, trainers, consultants (creator-style operations)",
        "Talent managers / agencies with 10–50 creator portfolios"
      ]
    },
    positioning: {
      title: "Positioning",
      aiNative: "AI-native, all-in-one – not just a webshop or link-in-bio, but a complete sales system",
      euroFirst: "Euro-first – pricing, VAT, regulations, multilingual, EUR focus",
      agencyFriendly: "Agency-friendly – multi-creator management, reports, revenue share"
    }
  },
  gtm: {
    title: "Go-to-Market",
    subtitle: "Eurozone + HU Focus",
    firstWave: {
      title: "First Wave",
      countries: "Hungary + 3–4 Eurozone countries (e.g., DE, AT, SK, SI) as pilots",
      target: "Micro/mid-tier creators already selling or planning to sell digital products"
    },
    channels: {
      title: "Channels",
      social: "Instagram/TikTok DM outreach with personalized messages",
      partnerships: "Partnership with creator agencies and talent managers",
      cases: "Case studies, \"from 0 to 5k/month\" type stories"
    },
    funnel: {
      title: "Monetization Funnel",
      free: "Free package with higher 9% fee (quick entry, frictionless)",
      upsell: "Upsell to Pro/Agency as transaction volume grows"
    }
  },
  financial: {
    title: "Financial Plan",
    subtitle: "3-year projection",
    assumptions: {
      title: "Assumptions",
      creators: "Paying creators by end of year",
      year1: "500 creators (Year 1)",
      year2: "2,000 creators (Year 2)",
      year3: "6,000 creators (Year 3)",
      avgRevenue: "Average monthly platform revenue: ~70 EUR / creator",
      gmv: "GMV / creator / year",
      gmvYear1: "3,000 EUR (Year 1)",
      gmvYear2: "6,000 EUR (Year 2)",
      gmvYear3: "10,000 EUR (Year 3)",
      platformFee: "Average platform fee: 4%"
    },
    revenue: {
      title: "Revenue",
      tableHeader: "Revenue type",
      saas: "SaaS subscriptions",
      transaction: "Transaction fees",
      total: "Total",
      year1: "Year 1",
      year2: "Year 2",
      year3: "Year 3"
    },
    costs: {
      title: "Costs",
      year1Description: "core team, development, infrastructure, initial marketing",
      year2Description: "team expansion, strong GTM",
      year3Description: "scaling, more countries, more languages"
    },
    ebitda: {
      title: "EBITDA",
      year1: "–110k EUR (Year 1)",
      year2: "+330k EUR (Year 2)",
      year3: "+2.22M EUR (Year 3)"
    }
  },
  funding: {
    title: "Funding Structure",
    target: "Target: ~2M EUR seed",
    equity: "Equity: max 10% (non-negotiable)",
    usage: {
      title: "Use of Funds",
      team: "50–55%: core team (CTO, developers, COO, partially marketing/growth)",
      acquisition: "20–25%: creator acquisition, paid and partner channels",
      infrastructure: "15–20%: infrastructure, AI costs, compliance, reserves"
    }
  },
  sources: {
    title: "Sources",
    items: [
      {
        title: "The Largest Influencer Study in Europe in 2023",
        url: "https://influencity.com/resources/studies/the-largest-influencer-study-of-europe-2023/",
        description: "European influencer statistics and market data"
      },
      {
        title: "The Influencer Marketing Statistics For 2024",
        url: "https://sociallypowerful.com/post/influencer-marketing-statistics-2024-2",
        description: "Influencer marketing trends and numbers"
      },
      {
        title: "Europe Creator Economy Market Size & Outlook, 2024-2033",
        url: "https://www.grandviewresearch.com/horizon/outlook/creator-economy-market/europe",
        description: "European creator economy market analysis"
      }
    ]
  },
  footer: {
    rights: "All rights reserved"
  }
};
